// Import react.
import * as React from 'react';
// Import hooks.
import {
	useList,
	UseListResults,
} from '@app/hooks/view';
// Import enumerations.
import {
	AppThemeColor,
	EligibilityValidationMessageType,
} from '@app/enumerations';
// Import material ui modules
import {
	CheckCircleOutlineOutlined,
	Description, 
	ReportProblemOutlined,
} from '@mui/icons-material';
// Import props.
import {
	SvgIconProps
} from '@mui/material';
// Import api models.
import {
	BindingAuthorityEligibilityResult,
	EligibilityValidationMessage,
} from '@app/models/api';

interface UseListBindingAuthorityEligibilityResultParameters {
	id: string;
	bindingAuthorityEligibilityResults: BindingAuthorityEligibilityResult[];
}

interface UseListBindingAuthorityEligibilityResultResults extends UseListResults {
	getValidationSourceProcess: (validationMessage: EligibilityValidationMessage) => string;
	getValidationSourceProcedure: (validationMessage: EligibilityValidationMessage) => string;
	getValidationEligibilityName: (validationMessage: EligibilityValidationMessage) => string;
	getValidationMessageType: (validationMessage: EligibilityValidationMessage) => string;
	getValidationFriendlyDescription: (validationMessage: EligibilityValidationMessage) => string;
	getValidationTechnicalDescription: (validationMessage: EligibilityValidationMessage) => string;
}

const useListBindingAuthorityEligibilityResult = (params: UseListBindingAuthorityEligibilityResultParameters): UseListBindingAuthorityEligibilityResultResults => {

	// #region exported methods

	/**
	 * Fetches validation source process.
	 * @param validationMessage
	 */
	const getValidationSourceProcess = (validationMessage: EligibilityValidationMessage): string => {
		return validationMessage.sourceProcess ?? String.empty();
	}

	/**
	 * Fetches validation source procedure.
	 * @param validationMessage
	 */
	const getValidationSourceProcedure = (validationMessage: EligibilityValidationMessage): string => {
		return validationMessage.sourceProcedure ?? String.empty();
	}

	/**
	 * Fetches validation eligibility name.
	 * @param validationMessage
	 */
	const getValidationEligibilityName = (validationMessage: EligibilityValidationMessage): string => {
		return validationMessage.eligibilityName ?? String.empty();
	}

	/**
	 * Fetches validation message type.
	 * @param validationMessage
	 */
	const getValidationMessageType = (validationMessage: EligibilityValidationMessage): string => {
		let messageType: EligibilityValidationMessageType = validationMessage.validationMessageType ?? EligibilityValidationMessageType.UNKNOWN;
		return EligibilityValidationMessageType[messageType];
	}

	/**
	 * Fetches validation friendly description.
	 * @param validationMessage
	 */
	const getValidationFriendlyDescription = (validationMessage: EligibilityValidationMessage): string => {
		return validationMessage.userFriendlyDescription ?? String.empty();
	}

	/**
	 * Fetches validation technical description.
	 * @param validationMessage
	 */
	const getValidationTechnicalDescription = (validationMessage: EligibilityValidationMessage): string => {
		return validationMessage.technicalDescription ?? String.empty();
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Checks to see if binding authority is eligible.
	 * @param bindingAuthorityEligibilityResult
	 */
	const isBindingAuthorityEligible = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult): boolean => {
		return bindingAuthorityEligibilityResult.isEligible !== null ? bindingAuthorityEligibilityResult.isEligible : false; 
	}

	/**
	 * Fetches the matching binding authority eligibility result based on the policy note email id.
	 * @param bindingAuthorityEligibilityResultId
	 */
	const getBindingAuthorityEligibilityResultListItemRecord = (bindingAuthorityEligibilityResultId: string): BindingAuthorityEligibilityResult | undefined => {
		return params.bindingAuthorityEligibilityResults.find((bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult) => bindingAuthorityEligibilityResult.id !== null && bindingAuthorityEligibilityResult.id.toString() === bindingAuthorityEligibilityResultId);
	}

	/**
	 *  Fetches the title of the binding authority eligibility result list item.
	 *  @param bindingAuthorityEligibilityResult
	 */
	const getHeaderTitle = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult, index: number): string => {
		return `BA# ${bindingAuthorityEligibilityResult.bindingAuthorityAgreement_Id}`;
	}

	/**
	 *  Fetches the sub-title of the binding authority eligibility result list item.
	 */
	const getSubtitle = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult): string | undefined => {
		return isBindingAuthorityEligible(bindingAuthorityEligibilityResult)
			? 'Eligible'
			: 'Ineligible';
	}

	/**
	 *  Fetches the sub-title icon.
	 */
	const getSubtitleIcon = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult): React.ComponentType<SvgIconProps> | undefined => {
		return isBindingAuthorityEligible(bindingAuthorityEligibilityResult)
			? CheckCircleOutlineOutlined
			: ReportProblemOutlined;
	}

	/**
	 *  Fetches the mobile sub-title icon.
	 */
	const getMobileIcon = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult): React.ComponentType<SvgIconProps> | undefined => {
		return Description;
	}

	/**
	 * Fetches the list item type based off the binding authority eligibililty status.
	 * @param bindingAuthorityEligibilityResult
	 */
	const getListItemType = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult): AppThemeColor => {
		return isBindingAuthorityEligible(bindingAuthorityEligibilityResult)
			? AppThemeColor.PRIMARY
			: AppThemeColor.WARNING;
	}

	/**
	 * Fetches the list item type mobile based off the binding authority eligibililty status.
	 * @param bindingAuthorityEligibilityResult
	 */
	const getListItemTypeMobile = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult): AppThemeColor => {
		return isBindingAuthorityEligible(bindingAuthorityEligibilityResult)
			? AppThemeColor.SUCCESS
			: AppThemeColor.WARNING;
	}

	// #region id generators

	const generateIdForBindingAuthorityEligibilityResultListItemElement = (bindingAuthorityEligibilityResult: BindingAuthorityEligibilityResult): string => {
		return `${params.id}-bindingAuthorityEligibilityResultListItem-${bindingAuthorityEligibilityResult.id}`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	// Setup the 'list' hook.
	const list: UseListResults = useList<BindingAuthorityEligibilityResult>({
		getListItemRecord: getBindingAuthorityEligibilityResultListItemRecord,
		generateListItemElementId: generateIdForBindingAuthorityEligibilityResultListItemElement,
		getHeaderTitle: getHeaderTitle,
		getSubtitle: getSubtitle,
		getSubtitleIcon: getSubtitleIcon,
		getMobileIcon: getMobileIcon,
		getListItemType: getListItemType,
		getListItemTypeMobile: getListItemTypeMobile,
		isCollapsible: true,
		isInitiallyCollapsed: true,
	});

	// #endregion variables


	return {
		...list,
		getValidationSourceProcess: getValidationSourceProcess,
		getValidationSourceProcedure: getValidationSourceProcedure,
		getValidationEligibilityName: getValidationEligibilityName,
		getValidationMessageType: getValidationMessageType,
		getValidationFriendlyDescription: getValidationFriendlyDescription,
		getValidationTechnicalDescription: getValidationTechnicalDescription,
	};
}

// Export parameters.
export {
	UseListBindingAuthorityEligibilityResultParameters,
}

// Export results.
export {
	UseListBindingAuthorityEligibilityResultResults,
}

export default useListBindingAuthorityEligibilityResult;